import React from "react";
import {
  Document,
  Image,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import { getFormattedUnit } from "config";

const NEEVAY_LOGO = "/logos/Neevay_Logo.png";

const COLORS = {
  navy: "#1A1A2E",
  navySoft: "#2A2A45",
  orange: "#FFA64F",
  orangeDark: "#E8831A",
  orangeSoft: "#FFF3E6",
  blueSoft: "#F5F8FF",
  panel: "#FBFCFE",
  border: "#DDE3EC",
  text: "#101828",
  muted: "#5F5E5A",
  mutedLight: "#8A8F9C",
  successBg: "#E8F5E9",
  success: "#2E7D32",
  danger: "#D32F2F",
};

const statusColor = {
  closed: "#FDE8E8",
  deleted: "#00FFFF",
  canceled: "#808080",
  hold: "#800080",
  open: "#BFFFBA",
  draft: "#FFFCB1",
  expired: "#9B1C1C",
  trash: "#9B1C1C",
  rework: "#D9F99D",
  "pending approval": "#FAD7A0",
};

const getStatusStyle = (status) => {
  const statusKey = String(status || "").toLowerCase();
  const darkStatuses = ["canceled", "hold", "expired", "trash"];
  return {
    backgroundColor: statusColor[statusKey] || COLORS.blueSoft,
    borderColor: statusColor[statusKey] || COLORS.border,
    color: darkStatuses.includes(statusKey) ? "#FFFFFF" : COLORS.text,
  };
};

// Define styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    paddingTop: 42,
    paddingRight: 42,
    paddingBottom: 38,
    paddingLeft: 42,
    backgroundColor: "#FFFFFF",
  },
  pageTopRule: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    backgroundColor: COLORS.navy,
  },
  pageHeader: {
    position: "absolute",
    top: 5,
    left: 42,
    right: 42,
    height: 42,
    backgroundColor: COLORS.navy,
    color: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
  },
  brandWrap: {
    flexDirection: "row",
    alignItems: "center",
  },
  brandN: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
    lineHeight: 20,
  },
  brandDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: COLORS.orange,
    marginLeft: 2,
    marginTop: 9,
  },
  headerTitleWrap: {
    alignItems: "flex-end",
  },
  headerTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
  },
  headerMeta: {
    fontSize: 7.5,
    color: "#FFDDBB",
    marginTop: 3,
  },
  pageFooter: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 22,
    backgroundColor: COLORS.orange,
    color: "#FFFFFF",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 42,
  },
  footerText: {
    fontSize: 7.2,
    color: "#FFFFFF",
  },
  section: {
    // marginBottom: 20,
  },
  firstPageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: COLORS.border,
  },
  firstPageRfqId: {
    fontSize: 11,
    color: COLORS.navy,
    fontFamily: "Helvetica-Bold",
  },
  firstPageRfqMeta: {
    fontSize: 7.5,
    color: COLORS.muted,
    marginTop: 3,
  },
  firstPageLogo: {
    width: 72,
    height: 27,
    objectFit: "contain",
  },
  titleCard: {
    backgroundColor: COLORS.navy,
    padding: 14,
    marginBottom: 14,
    borderRadius: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  titleEyebrow: {
    fontSize: 7.5,
    color: COLORS.orange,
    fontFamily: "Helvetica-Bold",
    marginBottom: 4,
    letterSpacing: 0.7,
  },
  titleText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontFamily: "Helvetica-Bold",
  },
  titleSubText: {
    fontSize: 8,
    color: "#D8D9E8",
    marginTop: 4,
  },
  titleBadge: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: COLORS.orange,
    color: "#FFFFFF",
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
  },
  mainHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 12,
    paddingBottom: 8,
    color: COLORS.text,
    borderBottomWidth: 1.5,
    borderBottomStyle: "solid",
    borderBottomColor: COLORS.orange,
    fontFamily: "Helvetica-Bold",
  },
  reportCard: {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.border,
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    marginBottom: 12,
  },
  reportCardBody: {
    padding: 10,
  },
  sectionHeaderRow: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: COLORS.orangeSoft,
    borderBottomWidth: 1.2,
    borderBottomStyle: "solid",
    borderBottomColor: COLORS.orange,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: 10.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.orangeDark,
  },
  sectionMeta: {
    fontSize: 7.5,
    color: COLORS.muted,
  },
  overviewGrid: {
    flexDirection: "row",
  },
  overviewColumn: {
    flex: 1,
    paddingRight: 10,
  },
  overviewColumnLast: {
    flex: 1,
    paddingLeft: 10,
    borderLeftWidth: 1,
    borderLeftStyle: "solid",
    borderLeftColor: COLORS.border,
  },
  fieldRow: {
    flexDirection: "row",
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: "#EEF1F6",
  },
  fieldLabel: {
    width: "38%",
    fontSize: 7.8,
    color: COLORS.muted,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
  },
  fieldValue: {
    flex: 1,
    fontSize: 8.8,
    color: COLORS.text,
    fontFamily: "Helvetica-Bold",
  },
  fieldValueRegular: {
    flex: 1,
    fontSize: 8.8,
    color: COLORS.text,
  },
  statusPill: {
    alignSelf: "flex-start",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.success,
    backgroundColor: COLORS.successBg,
    color: COLORS.success,
    fontSize: 8.3,
    fontFamily: "Helvetica-Bold",
  },
  metricsRow: {
    flexDirection: "row",
    marginTop: 10,
  },
  metricCard: {
    flex: 1,
    marginRight: 8,
    padding: 9,
    backgroundColor: COLORS.blueSoft,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.border,
    borderRadius: 4,
  },
  metricCardLast: {
    marginRight: 0,
  },
  metricLabel: {
    fontSize: 7.2,
    color: COLORS.muted,
    textTransform: "uppercase",
    fontFamily: "Helvetica-Bold",
    marginBottom: 5,
  },
  metricValue: {
    fontSize: 10.8,
    color: COLORS.navy,
    fontFamily: "Helvetica-Bold",
  },
  metricValueCompact: {
    fontSize: 9.2,
    lineHeight: 1.3,
  },
  notePanel: {
    marginTop: 10,
    padding: 9,
    backgroundColor: "#FFFBF7",
    borderLeftWidth: 3,
    borderLeftStyle: "solid",
    borderLeftColor: COLORS.orange,
  },
  vendorSummaryCard: {
    padding: 10,
    marginBottom: 9,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.border,
    backgroundColor: COLORS.panel,
    borderRadius: 5,
  },
  vendorSummaryTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  vendorName: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },
  rankBadge: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.orangeDark,
    backgroundColor: COLORS.orangeSoft,
    paddingVertical: 3,
    paddingHorizontal: 8,
  },
  vendorMetaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  quoteHeaderCard: {
    padding: 10,
    marginBottom: 10,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.border,
    backgroundColor: COLORS.panel,
    borderRadius: 5,
  },
  quoteHeaderRow: {
    flexDirection: "row",
    marginBottom: 6,
  },
  quoteHeaderCol: {
    flex: 1,
    paddingRight: 10,
  },
  quoteLabel: {
    fontSize: 7.4,
    color: COLORS.muted,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    marginBottom: 3,
  },
  quoteValue: {
    fontSize: 9.2,
    color: COLORS.text,
    fontFamily: "Helvetica-Bold",
  },
  tableCaption: {
    fontSize: 9.2,
    color: COLORS.navy,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
  },
  overviewHeader: {
    fontSize: 10,
    marginBottom: 10,
    padding: 8,
    color: COLORS.orangeDark,
    backgroundColor: COLORS.orangeSoft,
    borderBottomWidth: 1.5,
    borderBottomStyle: "solid",
    borderBottomColor: COLORS.orange,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },
  header: {
    fontSize: 10,
    marginBottom: 10,
    marginTop: 16,
    padding: 8,
    color: COLORS.orangeDark,
    backgroundColor: COLORS.orangeSoft,
    borderBottomWidth: 1.5,
    borderBottomStyle: "solid",
    borderBottomColor: COLORS.orange,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },
  topVendorsHeader: {
    fontSize: 10,
    marginBottom: 10,
    marginTop: 12,
    padding: 8,
    color: COLORS.orangeDark,
    backgroundColor: COLORS.orangeSoft,
    borderBottomWidth: 1.5,
    borderBottomStyle: "solid",
    borderBottomColor: COLORS.orange,
    fontWeight: "bold",
    // textAlign: "center",
    fontFamily: "Helvetica-Bold",
  },
  text: {
    fontSize: 9,
    marginTop: 4,
    marginBottom: 4,
    color: COLORS.text,
    // marginBottom: 5,
  },
  table: {
    display: "table",
    width: "100%",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: COLORS.border,
    borderBottom: 0,
    borderRadius: 4,
    marginBottom: 12,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderColor: COLORS.border,
  },
  tableCell: {
    padding: 6,
    fontSize: 8.4,
    color: COLORS.text,
    lineHeight: 1.25,
    // flex: 1,
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderColor: COLORS.border,
  },
  tableHeaderCell: {
    padding: 4,
    fontSize: 7.2,
    lineHeight: 1.15,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },
  tableCellLast: {
    borderRightWidth: 0, // Removes the border for the last cell
  },
  bold: {
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },
  horizontalLine: {
    height: 1.4,
    backgroundColor: COLORS.orange,
  },
});

const stripHtml = (html) => {
  if (!html) return "N/A";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "N/A";
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
};

const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const dateFormatter = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

const getLatestQuotationSet = (vendor) =>
  vendor?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || {};

const getCommercialDiscountPercent = (vendor) => {
  const percent = Number(getLatestQuotationSet(vendor)?.discountOnBasicAmountPercent || 0);
  if (!Number.isFinite(percent) || percent <= 0) return 0;
  return Math.min(100, percent);
};

const getCommercialDiscountAmount = (vendor) => {
  const amount = Number(getLatestQuotationSet(vendor)?.discountOnBasicAmount || 0);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return amount;
};

const shouldShowCommercialDiscount = (rfq, vendor) =>
  rfq?.rfqType === "material" &&
  rfq?.status === "CLOSED" &&
  (getCommercialDiscountPercent(vendor) > 0 || getCommercialDiscountAmount(vendor) > 0);

const PDFDocument = ({
  rfqData,
  poData,
  directoryName,
  generatedBy,
  summaryCounts,
}) => {
  const topVendors = rfqData?.rfq?.suppliers
    ?.filter((supplier) => supplier?.vendorQuotations?.rank)
    ?.sort(
      (a, b) =>
        parseInt(a.vendorQuotations.rank.replace("L", "")) -
        parseInt(b.vendorQuotations.rank.replace("L", "")),
    );

  // Get awarded suppliers data
  const awardedSuppliers = rfqData?.rfq?.suppliers?.filter(
    (supplier) => supplier.isAwarded,
  ) || [];
  const awardedVendorNames = awardedSuppliers.length
    ? awardedSuppliers
        .map((supplier) => {
          const rank = supplier?.vendorQuotations?.rank;
          return `${supplier?.businessName || "N/A"}${rank ? ` (${rank})` : ""}`;
        })
        .join(", ")
    : "N/A";
  const vendorInfoCount = rfqData?.rfq?.suppliers?.length || 0;
  const shouldCompactVendorPages = vendorInfoCount <= 1;

  // Check if RFQ is in pending approval or rework state
  const isPendingOrRework = ["PENDING APPROVAL", "REWORK"].includes(
    rfqData?.rfq?.status,
  );

  const materialUnitMap = {};
  if (rfqData?.rfq?.materials) {
    rfqData?.rfq?.materials.forEach((material) => {
      const cleanName = stripHtml(material?.name).toLowerCase().trim();
      materialUnitMap[cleanName] = material?.unit;
    });
  }

  const generatedOn = new Date().toLocaleDateString("en-GB");
  const rfqId = rfqData?.rfq?.rfqId || "N/A";
  const footerDirectoryName =
    directoryName ||
    rfqData?.rfq?.directoryName ||
    rfqData?.rfq?.clientName ||
    "Neevay";
  const generatedByName =
    generatedBy || rfqData?.rfq?.requesterName || "Neevay user";

  const PdfChrome = () => (
    <>
      <View style={styles.pageTopRule} fixed />
      <View style={styles.pageFooter} fixed>
        <Text style={styles.footerText}>RFQ #{rfqId}</Text>
        <Text style={styles.footerText}>
          ({footerDirectoryName}) | Generated by{" "}
          {generatedByName} on {generatedOn}
        </Text>
        <Text
          style={styles.footerText}
          render={({ pageNumber }) => `Page ${pageNumber}`}
        />
      </View>
    </>
  );

  const InfoRow = ({ label, children, valueStyle }) => (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <Text style={[styles.fieldValueRegular, valueStyle]}>{children}</Text>
    </View>
  );

  const MetricCard = ({ label, value, isLast, valueStyle }) => (
    <View style={[styles.metricCard, isLast ? styles.metricCardLast : null]}>
      <Text style={styles.metricLabel}>{label}</Text>
      <Text style={[styles.metricValue, valueStyle]}>{value}</Text>
    </View>
  );

  const SectionCard = ({ title, meta, children, style }) => (
    <View style={[styles.reportCard, style]}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {meta && <Text style={styles.sectionMeta}>{meta}</Text>}
      </View>
      <View style={styles.reportCardBody}>{children}</View>
    </View>
  );

  const vendorsInvitedCount =
    summaryCounts?.vendorsInvited ?? rfqData?.rfq?.suppliers?.length ?? 0;
  const quotationReceivedCount =
    summaryCounts?.quotationsReceived ??
    rfqData?.rfq?.suppliers?.filter((supplier) => supplier?.quotationReceived)
      ?.length ??
    0;
  const approvedByText = rfqData?.rfq?.approvalDetails?.length
    ? rfqData?.rfq?.approvalDetails
        .filter((item) => item.status === "APPROVED" && item.approvedBy?.name)
        .map((item) => item.approvedBy.name)
        .join(", ")
    : "N/A";
  const budgetDisplay =
    typeof rfqData?.rfq?.rfqBudget === "number"
      ? `Rs. ${new Intl.NumberFormat("en-IN").format(rfqData?.rfq?.rfqBudget)}`
      : "-";
  const createdAtDisplay = rfqData?.rfq?.createdAt
    ? new Date(rfqData?.rfq?.createdAt).toLocaleDateString("en-GB")
    : "N/A";
  const closingDateDisplay = rfqData?.rfq?.rfqClosingDetails
    ? formatDate(rfqData?.rfq?.rfqClosingDetails?.date)
    : "N/A";

  return (
    <Document>
      {/* Page 1: Requirement Overview, Closure Details, Saving Details */}
      <Page size="A4" style={styles.page}>
        <PdfChrome />
        <View style={styles.firstPageHeader}>
          <View>
            <Text style={styles.firstPageRfqId}>RFQ #{rfqId}</Text>
            <Text style={styles.firstPageRfqMeta}>Requirement summary report</Text>
          </View>
          <Image src={NEEVAY_LOGO} style={styles.firstPageLogo} />
        </View>
        <View style={styles.titleCard}>
          <View>
            <Text style={styles.titleEyebrow}>PROCUREMENT REPORT</Text>
            <Text style={styles.titleText}>Requirement Summary Document</Text>
            <Text style={styles.titleSubText}>
              {rfqData?.rfq?.name || "N/A"}
            </Text>
          </View>
        </View>
        {/* Requirement Overview */}
        <SectionCard
          title="RFQ Overview"
          meta={`${capitalizeFirstLetter(rfqData?.rfq?.rfqType) || "N/A"} request`}
        >
          <View style={styles.overviewGrid}>
            <View style={styles.overviewColumn}>
               <InfoRow label="Status">
                <Text
                  style={[
                    styles.statusPill,
                    getStatusStyle(rfqData?.rfq?.status),
                  ]}
                >
                  {rfqData?.rfq?.status || "N/A"}
                </Text>
              </InfoRow>
              <InfoRow label="Requirement" valueStyle={styles.fieldValue}>
                {rfqData?.rfq?.name || "N/A"}
              </InfoRow>
              <InfoRow label="Requirement Type" valueStyle={styles.fieldValue}>
                {capitalizeFirstLetter(rfqData?.rfq?.rfqType) || "N/A"}
              </InfoRow>
              <InfoRow label="Project Name" valueStyle={styles.fieldValue}>
                {rfqData?.rfq?.projectName || "N/A"}
              </InfoRow>
              <InfoRow label="Project Location" valueStyle={styles.fieldValue}>
                {rfqData?.rfq?.location || "N/A"}
              </InfoRow>
            </View>
            <View style={styles.overviewColumnLast}>
             
              <InfoRow label="RFQ ID" valueStyle={styles.fieldValue}>
                {rfqData?.rfq?.rfqId || "N/A"}
              </InfoRow>
              <InfoRow label="Created By" valueStyle={styles.fieldValue}>
                {rfqData?.rfq?.requesterName || "N/A"}
              </InfoRow>
              <InfoRow label="Created At" valueStyle={styles.fieldValue}>
                {createdAtDisplay}
              </InfoRow>
              <InfoRow label="Closed At" valueStyle={styles.fieldValue}>
                {closingDateDisplay}
              </InfoRow>
              <InfoRow label="Approved At" valueStyle={styles.fieldValue}>
                {closingDateDisplay}
              </InfoRow>
              <InfoRow label="Approved By" valueStyle={styles.fieldValue}>
                {approvedByText}
              </InfoRow>
            </View>
          </View>

          <View style={styles.metricsRow}>
            <MetricCard label="RFQ Budget" value={budgetDisplay} />
            <MetricCard
              label="Vendors Invited"
              value={vendorsInvitedCount}
            />
            <MetricCard
              label="Quotations Received"
              value={quotationReceivedCount}
              isLast
            />
          </View>

          <View style={styles.notePanel}>
            <Text style={styles.metricLabel}>Payment Terms</Text>
            <Text style={styles.text}>
              {stripHtml(rfqData?.rfq?.paymentTerms) || "N/A"}
            </Text>
          </View>
        </SectionCard>

        {/* Closure Details */}
        {rfqData?.rfq?.status === "CLOSED" && (
          <SectionCard title="Closure Details">
            {awardedSuppliers.length > 0 ? (
              <View style={styles.metricsRow}>
                <MetricCard
                  label="Awarded Vendor"
                  value={awardedVendorNames}
                  valueStyle={styles.metricValueCompact}
                />
                <MetricCard
                  label="Amount Excl. GST"
                  value={`Rs ${new Intl.NumberFormat("en-IN").format(
                    awardedSuppliers.reduce(
                      (sum, supplier) =>
                        sum +
                        (Number(
                          supplier?.vendorQuotations?.grandTotalWithoutGst,
                        ) || 0),
                      0,
                    ),
                  ) || "N/A"}`}
                />
                <MetricCard
                  label="Amount Incl. GST"
                  value={`Rs ${new Intl.NumberFormat("en-IN").format(
                    awardedSuppliers.reduce(
                      (sum, supplier) =>
                        sum +
                        (Number(
                          supplier?.vendorQuotations?.grandTotalWithGst,
                        ) || 0),
                      0,
                    ),
                  ) || "N/A"}`}
                  isLast
                />
              </View>
            ) : (
              <Text
                style={styles.text}
              >{`No closure details available as RFQ is in the ${rfqData?.rfq?.status} state.`}</Text>
            )}
          </SectionCard>
        )}

        {/* Saving Details */}
        {rfqData?.rfq?.status === "CLOSED" && (
          <SectionCard
            title="RFQ Saving Amount"
            meta="Based on first round lowest quotation vs final closure amount"
          >
            <Text style={styles.text}>
              {(() => {
                const getMinimumQuotation = () => {
                  if (!rfqData?.rfq?.suppliers) return 0;

                  const suppliersWithQuotes = rfqData.rfq.suppliers.filter(
                    (supplier) => supplier.quotationReceived,
                  );

                  const supplierQuotations = suppliersWithQuotes.map(
                    (supplier) => {
                      const firstQuotationSet =
                        supplier.vendorQuotations?.quotationSets?.[0];
                      if (firstQuotationSet) {
                        const totalWithoutGst =
                          firstQuotationSet.totalWithoutGst || 0;
                        const othersTotalWithoutGst =
                          firstQuotationSet.othersTotalWithoutGst || 0;
                        return totalWithoutGst + othersTotalWithoutGst;
                      }
                      return Infinity;
                    },
                  );

                  const minQuotation = Math.min(...supplierQuotations);
                  return minQuotation === Infinity ? 0 : minQuotation;
                };

                const calculateSavings = () => {
                  const awardedSuppliers = rfqData?.rfq?.suppliers?.filter(
                    (supplier) => supplier.isAwarded,
                  );
                  const totalAwardedAmount = awardedSuppliers.reduce(
                    (sum, supplier) => {
                      const awardedAmount =
                        Number(supplier.closingDetails?.[0]?.amount) || 0;
                      return sum + awardedAmount;
                    },
                    0,
                  );

                  const minQuotation = getMinimumQuotation();
                  const savingsAmount = minQuotation - totalAwardedAmount;

                  // Return 0 if savingsAmount is negative, otherwise return the actual savingsAmount
                  return savingsAmount < 0 ? 0 : savingsAmount;
                };

                const savings = calculateSavings();
                return savings > 0
                  ? `Saved Amount (excl. GST): Rs. ${new Intl.NumberFormat(
                      "en-IN",
                    ).format(savings)}`
                  : "0";
              })()}
            </Text>
          </SectionCard>
        )}
      {/* Quotation Overview */}
      {!shouldCompactVendorPages && (
        <View>
          <SectionCard
            title="Quotation Overview"
            meta={`${topVendors?.length || 0} ranked vendors`}
          >
            {topVendors?.map((vendor, index) => (
              <View key={index} style={styles.vendorSummaryCard}>
                <View style={styles.vendorSummaryTop}>
                  <Text style={styles.vendorName}>{vendor.businessName}</Text>
                  <Text style={styles.rankBadge}>
                    Rank {vendor?.vendorQuotations?.rank || "N/A"}
                  </Text>
                </View>

                <View style={styles.vendorMetaRow}>
                  <Text style={styles.text}>
                    Quotation Amount (excl. GST):{" "}
                    <Text
                      style={{
                        fontFamily: "Helvetica-Bold",
                        fontWeight: "bold",
                      }}
                    >
                      Rs{" "}
                      {new Intl.NumberFormat("en-IN").format(
                        vendor?.vendorQuotations?.grandTotalWithoutGst,
                      ) || "N/A"}
                    </Text>
                  </Text>
                </View>
                {/* Budget Deviation */}
                {rfqData?.rfq?.rfqBudget > 0 &&
                  (() => {
                    const budget = Number(rfqData?.rfq?.rfqBudget) || 0;
                    const quotation =
                      Number(vendor?.vendorQuotations?.grandTotalWithoutGst) || 0;

                    const diff = budget - quotation;
                    const isOverBudget = diff < 0;
                    const formattedDiff = new Intl.NumberFormat("en-IN", {
                      maximumFractionDigits: 0,
                    }).format(Math.abs(diff));

                    return (
                      <View style={{ flexDirection: "row", marginBottom: 4 }}>
                        <Text style={styles.text}>Budget Deviation: </Text>
                        <Text
                          style={[
                            styles.text,
                            {
                              fontFamily: "Helvetica-Bold",
                              fontWeight: "bold",
                              color: isOverBudget ? "#d32f2f" : "#2e7d32",
                            },
                          ]}
                        >
                          Rs {formattedDiff}{" "}
                          {isOverBudget ? "(Over Budget)" : "(Within Budget)"}
                        </Text>
                      </View>
                    );
                  })()}
                <Text style={styles.text}>
                  Payment Terms:{" "}
                  <Text
                    style={{ fontFamily: "Helvetica-Bold", fontWeight: "bold" }}
                  >
                    {vendor?.vendorQuotations?.quotationSets?.slice(-1)[0]
                      ?.counterPaymentTerms || "N/A"}
                  </Text>
                </Text>
              </View>
            ))}
            {/* Show vendors without received quotations */}
            {rfqData?.rfq?.suppliers
              ?.filter((supplier) => !supplier.quotationReceived)
              ?.map((vendor, index) => (
                <View
                  key={`no-quote-${index}`}
                  style={styles.vendorSummaryCard}
                >
                  <View style={styles.vendorSummaryTop}>
                    <Text style={styles.vendorName}>{vendor.businessName}</Text>
                    <Text style={styles.rankBadge}>Quotation Not Submitted</Text>
                  </View>
                </View>
              ))}
          </SectionCard>
        </View>
      )}

      {/* Pages for Material List Table and Other Charges */}
      <View
        break={!shouldCompactVendorPages}
        style={shouldCompactVendorPages ? { marginTop: 18 } : undefined}
      >
        <Text
          style={[
            styles.topVendorsHeader,
            { textAlign: "center" },
          ]}
        >
          Detailed Vendor Quotations:
        </Text>
        {topVendors?.map((vendor, index) => (
          <View style={styles.section}>
            <View style={styles.quoteHeaderCard}>
              <View style={styles.quoteHeaderRow}>
                <View style={styles.quoteHeaderCol}>
                  <Text style={styles.quoteLabel}>Vendor</Text>
                  <Text style={styles.quoteValue}>
                    {vendor.businessName} (Rank{" "}
                    {vendor?.vendorQuotations?.rank || "N/A"})
                  </Text>
                </View>
                <View style={styles.quoteHeaderCol}>
                  <Text style={styles.quoteLabel}>Quotation Excl. GST</Text>
                  <Text style={styles.quoteValue}>
                    Rs{" "}
                    {new Intl.NumberFormat("en-IN").format(
                      vendor?.vendorQuotations?.grandTotalWithoutGst,
                    ) || "N/A"}
                  </Text>
                </View>
                <View style={styles.quoteHeaderCol}>
                  <Text style={styles.quoteLabel}>Quotation Incl. GST</Text>
                  <Text style={styles.quoteValue}>
                    Rs{" "}
                    {new Intl.NumberFormat("en-IN").format(
                      vendor?.vendorQuotations?.grandTotalWithGst,
                    ) || "N/A"}
                  </Text>
                </View>
              </View>

              {rfqData?.rfq?.rfqBudget > 0 &&
                (() => {
                  const budget = Number(rfqData?.rfq?.rfqBudget) || 0;
                  const quotation =
                    Number(vendor?.vendorQuotations?.grandTotalWithoutGst) || 0;

                  const diff = budget - quotation;
                  const isOverBudget = diff < 0;

                  const formattedDiff = new Intl.NumberFormat("en-IN", {
                    maximumFractionDigits: 0,
                  }).format(Math.abs(diff));

                  return (
                    <Text style={styles.text}>
                      Budget Deviation:{" "}
                      <Text
                        style={{
                          fontFamily: "Helvetica-Bold",
                          color: isOverBudget ? COLORS.danger : COLORS.success,
                        }}
                      >
                        Rs {formattedDiff}{" "}
                        {isOverBudget ? "(Over Budget)" : "(Within Budget)"}
                      </Text>
                    </Text>
                  );
                })()}

              <Text style={styles.text}>
                Payment Terms:{" "}
                <Text
                  style={{ fontFamily: "Helvetica-Bold", fontWeight: "bold" }}
                >
                  {vendor?.vendorQuotations?.quotationSets?.slice(-1)[0]
                    ?.counterPaymentTerms || "N/A"}
                </Text>
              </Text>
              <Text style={styles.text}>
                Vendor Remarks:{" "}
                <Text
                  style={{ fontFamily: "Helvetica-Bold", fontWeight: "bold" }}
                >
                  {vendor?.vendorQuotations?.quotationSets?.slice(-1)[0]
                    ?.notes || "N/A"}
                </Text>
              </Text>
            </View>

            {/* Material List Table */}
            <Text style={styles.tableCaption}>
              {rfqData?.rfq?.rfqType === "material"
                ? "Material List & Quote:"
                : rfqData?.rfq?.rfqType === "labour"
                  ? "Labour Details & Quote:"
                  : rfqData?.rfq?.rfqType === "contractor"
                    ? "Providing and Fixing Details & Quote:"
                    : "Details & Quote:"}
            </Text>
            <View style={styles.table}>
              {/* Table Header */}
              {rfqData?.rfq?.rfqType === "material" && (
                <View
                  style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableHeaderCell,
                      { width: "4%" },
                    ]}
                  >
                    Sr #
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "20%" }]}
                  >
                    Product Name
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "5%" }]}
                  >
                    Qty
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "7%" }]}
                  >
                    Unit
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Budget Rate
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "9%" }]}
                  >
                    Budget Amount
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "7%" }]}
                  >
                    Net Rate
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "5%" }]}
                  >
                    {"Disc\n(%)"}
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "5%" }]}
                  >
                    {"GST\n(%)"}
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "11%" }]}
                  >
                    Amount (excl. GST)
                  </Text>
                  {/* <Text
                    style={[styles.tableCell, styles.bold, { width: "12%" }]}
                  >
                    Amount (incl. GST)
                  </Text> */}
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableHeaderCell,
                      styles.tableCellLast,
                      { width: "19%" },
                    ]}
                  >
                    Specification
                  </Text>
                </View>
              )}

              {rfqData?.rfq?.rfqType === "labour" && (
                <View
                  style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableHeaderCell,
                      { width: "4%" },
                    ]}
                  >
                    Sr #
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "25%" }]}
                  >
                    Skill
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "6%" }]}
                  >
                    Qty
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "7%" }]}
                  >
                    Unit
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Budget Rate
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "9%" }]}
                  >
                    Budget Amount
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Rate
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "6%" }]}
                  >
                    GST (%)
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "10%" }]}
                  >
                    Amount
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableHeaderCell,
                      styles.tableCellLast,
                      { width: "17%" },
                    ]}
                  >
                    Specifications
                  </Text>
                </View>
              )}

              {rfqData?.rfq?.rfqType === "contractor" && (
                <View
                  style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                >
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Sr #
                  </Text>

                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "28%" }]}
                  >
                    Section / Scope of Work
                  </Text>

                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "6%" }]}
                  >
                    Qty
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "6%" }]}
                  >
                    Unit
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Budget
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Budget Amount
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Material{"\n"} Rate
                  </Text>

                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "8%" }]}
                  >
                    Installation{"\n"} Rate
                  </Text>

                  <Text
                    style={[styles.tableCell, styles.tableHeaderCell, { width: "10%" }]}
                  >
                    Amount (excl. GST)
                  </Text>

                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableHeaderCell,
                      styles.tableCellLast,
                      { width: "10%" },
                    ]}
                  >
                    Amount (incl. GST)
                  </Text>
                </View>
              )}

              {/* Table Rows */}
              {rfqData?.rfq?.rfqType === "material" &&
                vendor?.vendorQuotations?.quotationSets
                  ?.slice(-1)[0]
                  ?.materialDetails?.map((item, idx) => {
                    const itemRate =
                      item.basePrice *
                      (1 - (parseFloat(item.discount) || 0) / 100);

                    const cleanItemName = stripHtml(item.name)
                      .toLowerCase()
                      .trim();

                    const unit = materialUnitMap[cleanItemName] || "N/A";

                    const materialBudgetMap = {};

                    if (rfqData?.rfq?.materials?.length) {
                      rfqData.rfq.materials.forEach((mat) => {
                        materialBudgetMap[mat._id] = {
                          budget: Number(mat?.budget) || 0,
                          budgetAmount: Number(mat?.budgetAmount) || 0,
                        };
                      });
                    }

                    const budget = materialBudgetMap[item.id]?.budget || 0;
                    const budgetAmount =
                      materialBudgetMap[item.id]?.budgetAmount || 0;

                    return (
                      <View key={idx} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: "4%" }]}>
                          {idx + 1}
                        </Text>
                        <Text style={[styles.tableCell, { width: "20%" }]}>
                          {stripHtml(item.name)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "5%" }]}>
                          {item.quantity}
                        </Text>
                        <Text style={[styles.tableCell, { width: "7%" }]}>
                          {getFormattedUnit(unit)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          {new Intl.NumberFormat("en-IN").format(budget)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "9%" }]}>
                          {new Intl.NumberFormat("en-IN").format(budgetAmount)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "7%" }]}>
                          {new Intl.NumberFormat("en-IN").format(itemRate)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "5%" }]}>
                          {/* {new Intl.NumberFormat("en-IN").format(item.discount)} */}
                          {item?.discount != null
                            ? `${
                                Math.floor(parseFloat(item?.discount) * 100) /
                                100
                              }%`
                            : "0%"}
                        </Text>
                        <Text style={[styles.tableCell, { width: "5%" }]}>
                          {item.gst}
                        </Text>
                        <Text style={[styles.tableCell, { width: "11%" }]}>
                          {new Intl.NumberFormat("en-IN").format(
                            item.priceWithoutGst,
                          )}
                        </Text>
                        {/* <Text style={[styles.tableCell, { width: "12%" }]}>
                          {new Intl.NumberFormat("en-IN").format(item.netPrice)}
                        </Text> */}
                        <Text
                          style={[
                            styles.tableCell,
                            styles.tableCellLast,
                            { width: "19%" },
                          ]}
                        >
                          {stripHtml(item.remark)}
                        </Text>
                      </View>
                    );
                  })}

              {rfqData?.rfq?.rfqType === "labour" &&
                vendor?.vendorQuotations?.quotationSets
                  ?.slice(-1)[0]
                  ?.labourItems?.map((item, idx) => {
                    const cleanSkill = stripHtml(item.skill)
                      .toLowerCase()
                      .trim();

                    const labourUnitMap = {};
                    if (rfqData?.rfq?.labourDetails) {
                      rfqData?.rfq?.labourDetails?.forEach((labour) => {
                        const cleanSkill = stripHtml(labour?.skill)
                          .toLowerCase()
                          .trim();
                        labourUnitMap[cleanSkill] = labour?.unit;
                      });
                    }
                    const unit = labourUnitMap[cleanSkill] || "";

                    const labourBudgetMap = {};

                    if (rfqData?.rfq?.labourDetails?.length) {
                      rfqData.rfq.labourDetails.forEach((lab) => {
                        labourBudgetMap[lab._id] = {
                          budget: Number(lab?.budget) || 0,
                          budgetAmount: Number(lab?.budgetAmount) || 0,
                        };
                      });
                    }

                    const budget = labourBudgetMap[item.id]?.budget || 0;
                    const budgetAmount =
                      labourBudgetMap[item.id]?.budgetAmount || 0;

                    return (
                      <View key={idx} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: "4%" }]}>
                          {idx + 1}
                        </Text>
                        <Text style={[styles.tableCell, { width: "25%" }]}>
                          {stripHtml(item.skill)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "6%" }]}>
                          {item.quantity}
                        </Text>
                        <Text style={[styles.tableCell, { width: "7%" }]}>
                          {getFormattedUnit(unit)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          {new Intl.NumberFormat("en-IN").format(budget)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "9%" }]}>
                          {new Intl.NumberFormat("en-IN").format(budgetAmount)}
                        </Text>
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          {new Intl.NumberFormat("en-IN").format(
                            item.labourRate,
                          )}
                        </Text>
                        <Text style={[styles.tableCell, { width: "6%" }]}>
                          {item.gst}
                        </Text>
                        <Text style={[styles.tableCell, { width: "10%" }]}>
                          {new Intl.NumberFormat("en-IN").format(
                            item.totalAmount,
                          )}
                        </Text>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.tableCellLast,
                            { width: "17%" },
                          ]}
                        >
                          {item.remarks || "N/A"}
                        </Text>
                      </View>
                    );
                  })}

              {/* Table Rows */}
              {vendor?.vendorQuotations?.quotationSets
                ?.slice(-1)[0]
                ?.contractorDetails?.map((section, sectionIdx) => {
                  let rowNumber = 1;
                  const rfqSection = rfqData?.rfq?.providingFixingDetails?.find(
                    (rfqSec) => rfqSec.sectionName === section.sectionName,
                  );

                  const sectionBudgetAmount =
                    Number(rfqSection?.budgetAmount) || 0;

                  return (
                    <View key={sectionIdx}>
                      {/* Section Header */}
                      <View
                        style={[
                          styles.tableRow,
                          { backgroundColor: COLORS.orangeSoft },
                        ]}
                      >
                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "8%" },
                          ]}
                        >
                          S{sectionIdx + 1}
                        </Text>

                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "28%" },
                          ]}
                        >
                          {section.sectionName || "N/A"}
                        </Text>

                        <Text style={[styles.tableCell, { width: "6%" }]}>
                          -
                        </Text>
                        <Text style={[styles.tableCell, { width: "6%" }]}>
                          -
                        </Text>
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          -
                        </Text>
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          {new Intl.NumberFormat("en-IN").format(
                            sectionBudgetAmount || 0,
                          )}
                        </Text>
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          -
                        </Text>
                        {/* <Text style={[styles.tableCell, { width: "8%" }]}>
                          -
                        </Text> */}
                        <Text style={[styles.tableCell, { width: "8%" }]}>
                          -
                        </Text>

                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "10%" },
                          ]}
                        >
                          {new Intl.NumberFormat("en-IN").format(
                            section.sectionTotalExclGst || 0,
                          )}
                        </Text>

                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "10%" },
                          ]}
                        >
                          {new Intl.NumberFormat("en-IN").format(
                            section.sectionTotalWithGst || 0,
                          )}
                        </Text>
                      </View>

                      {/* Scope Items */}
                      {section.scopes?.map((scope, scopeIdx) => {
                        const rfqScope = rfqSection?.scopes?.find(
                          (rfqSc) =>
                            stripHtml(rfqSc.scopeOfWork).trim() ===
                            stripHtml(scope.scopeOfWork).trim(),
                        );

                        const scopeBudget = Number(rfqScope?.budget) || 0;
                        const scopeBudgetAmount =
                          Number(rfqScope?.budgetAmount) || 0;
                        return (
                          <View key={scopeIdx}>
                            {/* Scope Row */}
                            <View
                              style={[
                                styles.tableRow,
                                { backgroundColor: COLORS.blueSoft },
                              ]}
                            >
                              <Text style={[styles.tableCell, { width: "8%" }]}>
                                SoW{`${sectionIdx + 1}.${scopeIdx + 1}`}
                              </Text>

                              <Text
                                style={[styles.tableCell, { width: "28%" }]}
                              >
                                {stripHtml(scope.scopeOfWork) || "N/A"}
                              </Text>

                              <Text style={[styles.tableCell, { width: "6%" }]}>
                                {scope.quantity || "N/A"}
                              </Text>

                              <Text style={[styles.tableCell, { width: "6%" }]}>
                                {getFormattedUnit(scope.unit) || "N/A"}
                              </Text>

                              <Text style={[styles.tableCell, { width: "8%" }]}>
                                {new Intl.NumberFormat("en-IN").format(
                                  scopeBudget || 0,
                                )}
                              </Text>

                              <Text style={[styles.tableCell, { width: "8%" }]}>
                                {new Intl.NumberFormat("en-IN").format(
                                  scopeBudgetAmount || 0,
                                )}
                              </Text>

                              <Text style={[styles.tableCell, { width: "8%" }]}>
                                {new Intl.NumberFormat("en-IN").format(
                                  scope.basePrice || 0,
                                )}
                              </Text>

                              <Text style={[styles.tableCell, { width: "8%" }]}>
                                {new Intl.NumberFormat("en-IN").format(
                                  scope.installationRate || 0,
                                )}
                              </Text>

                              <Text
                                style={[styles.tableCell, { width: "10%" }]}
                              >
                                {new Intl.NumberFormat("en-IN").format(
                                  scope.totalExclGst || 0,
                                )}
                              </Text>

                              <Text
                                style={[styles.tableCell, { width: "10%" }]}
                              >
                                {new Intl.NumberFormat("en-IN").format(
                                  scope.totalAmount || 0,
                                )}
                              </Text>
                            </View>

                            {/* Materials for this scope */}
                            {scope.materials?.map((material, materialIdx) => {
                              const rfqMaterial = rfqScope?.materials?.find(
                                (rfqMat) =>
                                  stripHtml(rfqMat.name).trim() ===
                                  stripHtml(material.name).trim(),
                              );

                              const materialBudget =
                                Number(rfqMaterial?.budget) || 0;
                              const materialBudgetAmount =
                                Number(rfqMaterial?.budgetAmount) || 0;
                              return (
                                <View style={styles.tableRow}>
                                  <Text
                                    style={[styles.tableCell, { width: "8%" }]}
                                  >
                                    M{`${scopeIdx + 1}.${materialIdx + 1}`}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "28%" }]}
                                  >
                                    {stripHtml(material.name) || "N/A"}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "6%" }]}
                                  >
                                    {material.quantity || "N/A"}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "6%" }]}
                                  >
                                    {getFormattedUnit(material.unit) || "N/A"}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "8%" }]}
                                  >
                                    {new Intl.NumberFormat("en-IN").format(
                                      materialBudget || 0,
                                    )}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "8%" }]}
                                  >
                                    {new Intl.NumberFormat("en-IN").format(
                                      materialBudgetAmount || 0,
                                    )}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "8%" }]}
                                  >
                                    {new Intl.NumberFormat("en-IN").format(
                                      material.basePrice || 0,
                                    )}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "8%" }]}
                                  >
                                    {new Intl.NumberFormat("en-IN").format(
                                      material.installationRate || 0,
                                    )}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "10%" }]}
                                  >
                                    {new Intl.NumberFormat("en-IN").format(
                                      material.totalExclGst || 0,
                                    )}
                                  </Text>

                                  <Text
                                    style={[styles.tableCell, { width: "10%" }]}
                                  >
                                    {new Intl.NumberFormat("en-IN").format(
                                      material.totalWithGst || 0,
                                    )}
                                  </Text>
                                </View>
                              );
                            })}
                          </View>
                        );
                      })}
                    </View>
                  );
                })}
            </View>

            {shouldShowCommercialDiscount(rfqData?.rfq, vendor) && (
              <View style={[styles.table, { marginTop: 4 }]}>
                <View
                  style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      styles.bold,
                      { width: "50%" },
                    ]}
                  >
                    Commercial Discount
                  </Text>
                  <Text style={[styles.tableCell, { width: "25%" }]}>
                    {getCommercialDiscountPercent(vendor)}%
                  </Text>
                  <Text
                    style={[
                      styles.tableCell,
                      styles.tableCellLast,
                      { width: "25%" },
                    ]}
                  >
                    Rs.{" "}
                    {new Intl.NumberFormat("en-IN", {
                      maximumFractionDigits: 2,
                    }).format(getCommercialDiscountAmount(vendor))}
                  </Text>
                </View>
              </View>
            )}

            {/* Other Charges Table */}
            {Object.entries(
              vendor?.vendorQuotations?.quotationSets?.slice(-1)[0]
                ?.otherCharges || {},
            ).some(([chargeName, details]) => details.mrp > 0) && ( // Check if there's any charge with mrp > 0
              <>
                <Text style={styles.text}>Other Charges</Text>{" "}
                {/* Display the heading only if there's a charge with mrp > 0 */}
                <View style={styles.table}>
                  <View
                    style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                  >
                    <Text
                      style={[styles.tableCell, styles.bold, { width: "40%" }]}
                    >
                      Particular
                    </Text>
                    <Text
                      style={[styles.tableCell, styles.bold, { width: "20%" }]}
                    >
                      Price
                    </Text>
                    <Text
                      style={[styles.tableCell, styles.bold, { width: "20%" }]}
                    >
                      GST
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.bold,
                        styles.tableCellLast,
                        { width: "20%" },
                      ]}
                    >
                      Total
                    </Text>
                  </View>
                  {Object.entries(
                    vendor?.vendorQuotations?.quotationSets?.slice(-1)[0]
                      ?.otherCharges || {},
                  )
                    .filter(([chargeName, details]) => details.mrp > 0) // Filter charges where mrp > 0
                    .map(([chargeName, details], idx) => (
                      <View key={idx} style={styles.tableRow}>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "40%" },
                          ]}
                        >
                          {chargeName}
                        </Text>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "20%" },
                          ]}
                        >
                          {new Intl.NumberFormat("en-IN").format(details.mrp)}
                        </Text>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.bold,
                            { width: "20%" },
                          ]}
                        >
                          {details.gst}
                        </Text>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.tableCellLast,
                            { width: "20%" },
                          ]}
                        >
                          {new Intl.NumberFormat("en-IN").format(
                            details.totalCost,
                          )}
                        </Text>
                      </View>
                    ))}
                </View>
              </>
            )}
            <View
              style={[
                styles.horizontalLine,
                { marginTop: "10px", marginBottom: "10px" },
              ]}
            ></View>
          </View>
        ))}
      </View>

      {/* Awardee / approval details */}
      <View style={{ marginTop: 18 }}>
        {(awardedSuppliers?.length > 0 || isPendingOrRework) && (
          <View style={styles.section}>
            <Text style={[styles.mainHeader, { marginBottom: 10 }]}>
              {isPendingOrRework
                ? "Proposed Closure Details"
                : "RFQ Awardee Details"}
            </Text>

            {/* Awardee Table with exactly 4 columns */}
            <View style={styles.table}>
              {/* Table Header */}
              <View
                style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
              >
                <Text style={[styles.tableCell, styles.bold, { width: "25%" }]}>
                  Business Name
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "15%" }]}>
                  Mobile Number
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "20%" }]}>
                  Closing Amount
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "20%" }]}>
                  Awarded By
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.bold,
                    styles.tableCellLast,
                    { width: "20%" },
                  ]}
                >
                  Date & Time
                </Text>
              </View>

              {/* Table Rows */}
              {awardedSuppliers?.map((awardee) => {
                const contactNumber =
                  awardee?.contactDetails?.[0]?.mobile || "N/A";
                const closingDetails = awardee?.closingDetails?.[0];

                return (
                  <View key={awardee.supplierId} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: "25%" }]}>
                      {awardee.businessName}
                    </Text>
                    <Text style={[styles.tableCell, { width: "15%" }]}>
                      {contactNumber}
                    </Text>
                    <Text style={[styles.tableCell, { width: "20%" }]}>
                      {new Intl.NumberFormat("en-IN").format(
                        Number(
                          closingDetails?.amount ||
                            awardee.vendorQuotations?.grandTotalWithoutGst ||
                            0,
                        ),
                      )}
                    </Text>
                    <Text style={[styles.tableCell, { width: "20%" }]}>
                      {rfqData?.rfq?.rfqClosingDetails?.name ||
                        rfqData?.rfq?.requesterName ||
                        "N/A"}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.tableCellLast,
                        { width: "20%" },
                      ]}
                    >
                      {rfqData?.rfq?.rfqClosingDetails?.date
                        ? formatDate(rfqData.rfq.rfqClosingDetails.date)
                        : "N/A"}
                    </Text>
                  </View>
                );
              })}
            </View>

            {/* Awarded/Proposed By Information */}
            {/* <View style={{marginTop: 15}}>
                            <Text style={[styles.text, {fontFamily: "Helvetica-Bold"}]}>
                                {isPendingOrRework ? "Proposed by" : "Awarded by"}:
                            </Text>
                            <Text style={styles.text}>
                                {rfqData?.rfq?.rfqClosingDetails?.name || rfqData?.rfq?.requesterName || "N/A"}
                            </Text>
                            <Text style={[styles.text, {marginTop: 5}]}>
                                Date: {rfqData?.rfq?.rfqClosingDetails?.date ? 
                                    formatDate(rfqData.rfq.rfqClosingDetails.date) : "N/A"}
                            </Text>
                        </View> */}

            {/* Show rejection details if in REWORK status */}
            {rfqData?.rfq?.status === "REWORK" &&
              rfqData?.rfq?.rejectionLogs?.length > 0 && (
                <View style={{ marginTop: 15 }}>
                  <Text style={[styles.mainHeader, { marginBottom: 10 }]}>
                    Rejection Details
                  </Text>
                  <View style={[styles.table]}>
                    <View
                      style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                    >
                      <Text
                        style={[
                          styles.tableCell,
                          styles.bold,
                          { width: "25%" },
                        ]}
                      >
                        Rejected By
                      </Text>
                      <Text
                        style={[
                          styles.tableCell,
                          styles.bold,
                          { width: "25%" },
                        ]}
                      >
                        Date & Time
                      </Text>
                      <Text
                        style={[
                          styles.tableCell,
                          styles.bold,
                          styles.tableCellLast,
                          { width: "50%" },
                        ]}
                      >
                        Remarks
                      </Text>
                    </View>
                    {rfqData.rfq.rejectionLogs.map((log, index) => (
                      <View key={index} style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: "25%" }]}>
                          {log.userName || rfqData?.rfq?.requesterName}
                        </Text>
                        <Text style={[styles.tableCell, { width: "25%" }]}>
                          {log.date ? formatDate(log.date) : "N/A"}
                        </Text>
                        <Text
                          style={[
                            styles.tableCell,
                            styles.tableCellLast,
                            { width: "50%" },
                          ]}
                        >
                          {log.remark || "N/A"}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>
              )}
          </View>
        )}
        {/* Comments for Approver Section */}
        {rfqData?.rfq?.commentsToApprover?.length > 0 && (
          <View style={{ marginTop: 20 }}>
            <Text style={[styles.mainHeader, { marginBottom: 10 }]}>
              Comments for Approver
            </Text>

            <View style={styles.table}>
              <View
                style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
              >
                <Text style={[styles.tableCell, styles.bold, { width: "25%" }]}>
                  Added By
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "25%" }]}>
                  Date & Time
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    styles.bold,
                    styles.tableCellLast,
                    { width: "50%" },
                  ]}
                >
                  Comment
                </Text>
              </View>

              {rfqData?.rfq?.commentsToApprover?.map((commentObj, index) => {
                // Format date and time
                const formatCommentDateTime = () => {
                  try {
                    const dateTime = new Date(
                      `${commentObj?.date}T${commentObj?.time}`,
                    );
                    const timeStr = dateTime.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    const dateStr = `${dateTime.getDate()}/${
                      dateTime.getMonth() + 1
                    }/${dateTime.getFullYear()}`;
                    return `${timeStr} ${dateStr}`;
                  } catch (error) {
                    return "N/A";
                  }
                };

                return (
                  <View key={index} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: "25%" }]}>
                      {commentObj?.addedByName || "N/A"}
                    </Text>
                    <Text style={[styles.tableCell, { width: "25%" }]}>
                      {formatCommentDateTime()}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.tableCellLast,
                        { width: "50%" },
                      ]}
                    >
                      {stripHtml(commentObj?.comment) || "N/A"}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {rfqData?.rfq?.approvalDetails?.length > 0 && (
          // <Page size="A4" style={styles.page} orientation="landscape">
          <View style={{ marginTop: 20 }}>
            <Text style={[styles.mainHeader, { marginBottom: 10 }]}>
              RFQ Approval Details
            </Text>

            {/* Approval Details Table */}
            <View style={styles.table}>
              {/* Table Header */}
              <View
                style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
              >
                <Text style={[styles.tableCell, styles.bold, { width: "10%" }]}>
                  Level
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "30%" }]}>
                  Approver
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "20%" }]}>
                  Status
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "20%" }]}>
                  Approved Amount
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "20%" }]}>
                  Approved At
                </Text>
              </View>

              {/* Table Rows */}
              {rfqData.rfq.approvalDetails.map((approval, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { width: "10%" }]}>
                    {approval.level}
                  </Text>
                  <Text style={[styles.tableCell, { width: "30%" }]}>
                    {(approval?.status === "PENDING"
                      ? approval?.approvers[0]?.name
                      : approval.approvedBy?.name) || "N/A"}
                  </Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    {rfqData?.rfq?.status === "REWORK" &&
                    approval?.status !== "APPROVED"
                      ? "REWORK"
                      : approval?.status}
                  </Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    Rs.{" "}
                    {new Intl.NumberFormat("en-IN").format(
                      approval.approvedAmount || 0,
                    )}
                  </Text>
                  <Text style={[styles.tableCell, { width: "20%" }]}>
                    {approval.approvedAt
                      ? formatDate(approval.approvedAt)
                      : "N/A"}
                  </Text>
                </View>
              ))}
            </View>

            {/* Additional approval information if needed */}
            {/* {rfqData?.rfq?.commentsToApprover?.length > 0 && (
                            <>
                                <Text style={[styles.text, { marginTop: 10 }]}>Comments to Approver:</Text>
                                {rfqData.rfq.commentsToApprover.map((comment, index) => (
                                    <View key={index} style={{ marginBottom: 5 }}>
                                        <Text style={styles.text}>
                                            {comment.addedByName || "Anonymous"}: {stripHtml(comment.comment)}
                                        </Text>
                                        <Text style={[styles.text, { fontSize: 8 }]}>
                                            {comment.date} {comment.time}
                                        </Text>
                                    </View>
                                ))}
                            </>
                        )} */}
          </View>
          // </Page>
        )}
      </View>
      </Page>

      {/* Page for Purchase Orders */}
      {poData &&
        poData?.length > 0 && ( // Check poData instead of rfqData.purchaseOrders
          <Page size="A4" style={styles.page}>
            <PdfChrome />
            <View style={styles.section}>
              <Text style={[styles.mainHeader, { marginBottom: 10 }]}>
                {rfqData?.rfq?.rfqType === "material"
                  ? "Purchase Orders"
                  : "Work Orders"}
              </Text>

              {/* Purchase Orders Table */}
              <View style={styles.table}>
                {/* Table Header */}
                <View
                  style={[styles.tableRow, { backgroundColor: COLORS.orangeSoft }]}
                >
                  <Text
                    style={[styles.tableCell, styles.bold, { width: "5%" }]}
                  >
                    Sr #
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.bold, { width: "10%" }]}
                  >
                    Create Date
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.bold, { width: "16%" }]}
                  >
                    {rfqData?.rfq?.rfqType === "material"
                      ? "PO Number"
                      : "WO Number"}
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.bold, { width: "35%" }]}
                  >
                    Vendor Name
                  </Text>
                  <Text
                    style={[styles.tableCell, styles.bold, { width: "12%" }]}
                  >
                    Total Basic Amount
                  </Text>
                  {/* <Text style={[styles.tableCell, styles.bold, { width: "12%" }]}>GST Amount</Text>
                <Text style={[styles.tableCell, styles.bold, { width: "12%" }]}>Total Amount</Text> */}
                  <Text
                    style={[styles.tableCell, styles.bold, { width: "28%" }]}
                  >
                    Created By
                  </Text>
                  {/* <Text style={[styles.tableCell, styles.bold, styles.tableCellLast, { width: "12%" }]}>Updated By</Text> */}
                </View>

                {/* Table Rows */}
                {poData?.map((po, index) => (
                  <View key={po.poId} style={styles.tableRow}>
                    <Text style={[styles.tableCell, { width: "5%" }]}>
                      {index + 1}
                    </Text>
                    <Text style={[styles.tableCell, { width: "10%" }]}>
                      {new Date(po?.createdOn).toLocaleDateString("en-GB") ||
                        "N/A"}
                    </Text>
                    <Text style={[styles.tableCell, { width: "16%" }]}>
                      {po?.poNumber || "N/A"}
                    </Text>
                    <Text style={[styles.tableCell, { width: "35%" }]}>
                      {stripHtml(po?.vendorName) || "N/A"}
                    </Text>
                    <Text style={[styles.tableCell, { width: "12%" }]}>
                      Rs.{" "}
                      {po?.poAmount
                        ? new Intl.NumberFormat("en-IN").format(po?.poAmount)
                        : "N/A"}
                    </Text>
                    <Text
                      style={[
                        styles.tableCell,
                        styles.tableCellLast,
                        { width: "28%" },
                      ]}
                    >
                      {po?.createdByName || "N/A"}
                    </Text>
                    {/* <Text style={[styles.tableCell, styles.tableCellLast, { width: "12%" }]}>
                    {po.updatedBy || "N/A"}
                  </Text> */}
                  </View>
                ))}
              </View>
            </View>
          </Page>
        )}
    </Document>
  );
};

export default PDFDocument;
